"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { signOut, useSession } from "next-auth/react";
import { LogOut, User, KeyRound, ChevronDown } from "lucide-react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DashboardSwitcher } from "./dashboard-switcher";
import type { UserInfo } from "@/types/api";

function buildBreadcrumbs(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  return segments.map((segment, index) => ({
    label: segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    href: "/" + segments.slice(0, index + 1).join("/"),
    isLast: index === segments.length - 1,
  }));
}

export function AdminHeader() {
  const pathname = usePathname();
  const { data: session } = useSession();
  const user = session?.user as UserInfo | undefined;
  const breadcrumbs = buildBreadcrumbs(pathname ?? "/admin");

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  // Format last login date
  const lastLogin = (user as UserInfo & { last_login?: string })?.last_login;
  const lastLoginFormatted = lastLogin
    ? new Date(lastLogin).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <header
      className="flex h-14 shrink-0 items-center gap-2 border-b px-4"
      style={{ background: "#1d1f23", borderColor: "hsl(240 20% 20%)" }}
    >
      <SidebarTrigger
        className="-ml-1 hover:bg-white/5 text-muted-foreground hover:text-foreground"
      />
      <Separator orientation="vertical" className="mr-2 h-4 opacity-30" />

      {/* Breadcrumbs */}
      <Breadcrumb className="flex-1 hidden sm:flex">
        <BreadcrumbList>
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.href} className="contents">
              {i > 0 && <BreadcrumbSeparator className="text-muted-foreground/40" />}
              <BreadcrumbItem>
                {crumb.isLast ? (
                  <BreadcrumbPage className="text-foreground font-medium">{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link href={crumb.href} className="text-muted-foreground hover:text-primary transition-colors">
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </span>
          ))}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Welcome message — matches Angular "Welcome: Name" */}
      {user?.name && (
        <div className="hidden md:flex flex-col text-right leading-tight mr-1">
          <span className="text-xs font-medium text-foreground/80">
            Welcome,{" "}
            <span style={{ color: "#ffa800" }} className="font-semibold">{user.name}</span>
          </span>
          {lastLoginFormatted && (
            <span className="text-[10px] text-muted-foreground">Last login: {lastLoginFormatted}</span>
          )}
        </div>
      )}

      {/* Dashboard switcher */}
      <DashboardSwitcher />

      {/* User dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/5 focus:outline-none"
            aria-label="User menu"
          >
            <Avatar className="size-7 shrink-0">
              <AvatarFallback
                className="text-[10px] font-semibold"
                style={{
                  background: "linear-gradient(135deg, rgba(255,168,0,0.25), rgba(255,126,0,0.12))",
                  color: "#ffa800",
                  border: "1px solid rgba(255,168,0,0.4)",
                }}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            <ChevronDown className="size-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-56"
          style={{ background: "#1d1f23", border: "1px solid hsl(240 20% 22%)" }}
        >
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-0.5">
              <p className="text-sm font-semibold text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator style={{ background: "hsl(240 26% 20%)" }} />
          <DropdownMenuItem asChild>
            <Link href="/admin/account" className="cursor-pointer">
              <User className="mr-2 size-4" />
              My Account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/admin/change-password" className="cursor-pointer">
              <KeyRound className="mr-2 size-4" />
              Change Password
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator style={{ background: "hsl(240 26% 20%)" }} />
          <DropdownMenuItem
            onClick={() => signOut({ callbackUrl: "/login" })}
            className="text-destructive focus:text-destructive cursor-pointer"
          >
            <LogOut className="mr-2 size-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
